import { BREEDER_FOOD } from './foods.ts';
import { PRODUCTS } from './products.ts';
import type { ShoppingCategory } from './types.ts';

// Shopping list. Prices are rough UK guides (autumn 2026) and vary a lot.
// "Before home" items should be ready before he arrives; the rest can wait.

const CATALOGUE: ShoppingCategory[] = [
  {
    id: 'sleep',
    title: "Sleep and crate",
    items: [
      {
        id: 'crate',
        name: "Crate",
        why: "His safe den for naps and nights. Crate or closed-room sleeping was linked to fewer separation problems later.",
        priority: 'essential',
        beforeHome: true,
        price: "£40–90",
        lookFor: "Metal wire crate about 36\" (91 cm) with a divider panel, so it’s small now and grows with him. Two doors are handy.",
      },
      {
        id: 'crate-bed',
        name: "Crate bed or vet bedding",
        why: "Comfy, washable bedding for inside the crate. You’ll wash it often in the first weeks.",
        priority: 'essential',
        beforeHome: true,
        price: "£15–30",
        lookFor: "Two pieces of fleece-backed vet bedding or a chew-resistant crate mat, so one can be in the wash.",
      },
      {
        id: 'crate-cover',
        name: "Crate cover",
        why: "Covering three sides makes the crate feel like a cosy den and helps him sleep.",
        priority: 'recommended',
        beforeHome: true,
        price: "£15–30",
        lookFor: "A fitted cover for your crate size, or an old breathable blanket. Leave the front open and let air circulate.",
      },
      {
        id: 'bed',
        name: "Daytime bed",
        why: "A second bed in the living room for settling and “On your bed”.",
        priority: 'recommended',
        beforeHome: true,
        price: "£20–50",
        lookFor: "Washable cover, raised sides for a snuggler. Skip the expensive one until he’s past the chewing stage.",
      },
      {
        id: 'blankets',
        name: "Soft blankets",
        why: "For the sofa, the car and his bed. Take one to the breeder beforehand to pick up his litter’s smell.",
        priority: 'essential',
        beforeHome: true,
        price: "£5–15",
        lookFor: "A few cheap fleece blankets.",
      },
      {
        id: 'playpen',
        name: "Playpen",
        why: "A safe space for when you can’t watch him for a few minutes, and for early alone-time practice.",
        priority: 'optional',
        price: "£40–80",
        lookFor: "Metal panels at least 80 cm high that can attach to the crate door.",
      },
      {
        id: 'comfort-toy',
        name: "Heartbeat or comfort toy",
        why: "Some puppies find a warm, soft toy with a gentle heartbeat comforting in the first nights away from their litter.",
        priority: 'optional',
        beforeHome: true,
        price: "£20–35",
        lookFor: "A heartbeat puppy toy with a removable heat pack, or simply a soft toy rubbed on the litter blanket.",
      },
    ],
  },
  {
    id: 'feeding',
    title: "Food and feeding",
    items: [
      {
        id: 'breeder-food',
        name: "Breeder’s puppy food",
        why: "Keeping the same food for the first week or two avoids upset tummies while he settles in.",
        priority: 'essential',
        beforeHome: true,
        price: "£27.49 for 10 kg",
        lookFor: "Ask the breeder how much he’s having a day, and for a few days’ supply to bring home. The feeding chart and calories per 100 g are on the bag.",
        // What Arlo's breeder feeds him.
        pick: {
          product: 'Country Dog Premium Puppy, Hypoallergenic Chicken & Rice (10 kg)',
          link: BREEDER_FOOD.link,
          note: 'What the breeder feeds him. Mole Valley Farmers, delivered (also a 2 kg bag).',
        },
      },
      {
        id: 'new-food',
        name: "The food you’ll move to",
        why: "If you want to change, do it gradually over 7–10 days (25% → 50% → 75% → 100%).",
        priority: 'recommended',
        price: "£30–60 a month",
        lookFor: "A complete puppy food for medium breeds. Ask your vet if unsure. Stay on puppy food until about 12 months.",
      },
      {
        id: 'spaniel-bowl',
        name: "Spaniel bowl or ear snood",
        why: "Stops his long ears dangling in his food and water, which keeps them cleaner and drier.",
        priority: 'recommended',
        cocker: true,
        price: "£8–20",
        lookFor: "A tall, narrow “spaniel bowl” with a tapered top, or a stretchy snood for mealtimes.",
      },
      {
        id: 'slow-feeder',
        name: "Slow feeder",
        why: "Stops gulping and makes each meal a little brain game. Cockers are known for inhaling food.",
        priority: 'recommended',
        cocker: true,
        price: "£8–15",
        lookFor: "A non-slip slow-feeder bowl with shallow ridges he can manage as a small puppy.",
      },
      {
        id: 'water-bowl',
        name: "Water bowl",
        why: "Fresh water should be available at all times, including in his crate.",
        priority: 'essential',
        beforeHome: true,
        price: "£5–15",
        lookFor: "Heavy ceramic or stainless-steel bowl that won’t tip, plus a clip-on bowl for the crate.",
      },
      {
        id: 'kitchen-scale',
        name: "Digital kitchen scale",
        why: "Weighing each meal keeps him lean and makes it easy to take training treats out of his daily ration.",
        priority: 'essential',
        beforeHome: true,
        price: "£10–15",
        lookFor: "Any digital kitchen scale that weighs in grams with a tare button.",
      },
      {
        id: 'food-bin',
        name: "Airtight food container",
        why: "Keeps food fresh and stops a determined spaniel helping himself.",
        priority: 'recommended',
        beforeHome: true,
        price: "£10–25",
        lookFor: "A container with a locking lid. Keep the food in its original bag inside it, so you have the batch and use-by details.",
      },
      {
        id: 'training-treats',
        name: "Small, soft training treats",
        why: "You’ll use hundreds a day. Tiny, smelly treats make training quick and easy.",
        priority: 'essential',
        beforeHome: true,
        price: "£3–6 a bag",
        lookFor: "Soft treats you can break into pea-sized bits. Add some of his kibble and a few high-value treats (like chicken) for recall.",
      },
      {
        id: 'chew-treats',
        name: "Puppy-safe long-lasting chews",
        why: "Chewing helps with teething and helps him settle in the crate.",
        priority: 'recommended',
        price: "£5–15",
        lookFor: "Puppy-appropriate natural chews. Supervise, and avoid cooked bones and rawhide.",
      },
    ],
  },
  {
    id: 'walking',
    title: "Walking and ID",
    items: [
      {
        id: 'collar',
        name: "Puppy collar",
        why: "He must wear a collar with an ID tag in public. Puppies grow fast, so expect to buy two or three.",
        priority: 'essential',
        beforeHome: true,
        price: "£8–15",
        lookFor: "Lightweight, adjustable, with a buckle. You should be able to fit two fingers underneath.",
      },
      {
        id: 'id-tag',
        name: "ID tag",
        why: "The law (Control of Dogs Order 1992) requires your name and address on his collar or tag in public, even though he’s microchipped.",
        priority: 'essential',
        beforeHome: true,
        price: "£5–15",
        lookFor: "Engraved with your surname, first line of your address and postcode, plus a phone number. Consider not putting his name on it.",
      },
      {
        id: 'harness',
        name: "Y-front harness",
        why: "Takes pressure off his neck while he learns loose-lead walking, and is essential with a long line.",
        priority: 'essential',
        price: "£15–40",
        lookFor: "Y-shaped front that sits clear of his shoulders, adjustable at neck and chest, with front and back clips. Avoid straps across the front of the shoulders.",
      },
      {
        id: 'lead',
        name: "Lead",
        why: "Every day, every walk. A lightweight one is best while he’s tiny.",
        priority: 'essential',
        beforeHome: true,
        price: "£10–25",
        lookFor: "1.5–2 m, lightweight, with a comfortable handle. Double-ended training leads are versatile. Avoid extendable leads.",
      },
      {
        id: 'long-line',
        name: "Long line",
        why: "Lets him explore and practise recall safely before he’s reliable off lead.",
        priority: 'essential',
        cocker: true,
        price: "£10–25",
        lookFor: "5–10 m biothane line; it doesn’t soak up water or mud. Always clip to the harness, never the collar.",
      },
      {
        id: 'poo-bags',
        name: "Poo bags and holder",
        why: "You must clear up after him in public places.",
        priority: 'essential',
        beforeHome: true,
        price: "£5–10",
        lookFor: "Compostable or recycled bags and a clip-on dispenser for the lead.",
      },
      {
        id: 'treat-pouch',
        name: "Treat pouch",
        why: "Makes it quick to reward him at the right moment, on walks and at his toilet spot.",
        priority: 'recommended',
        beforeHome: true,
        price: "£8–20",
        lookFor: "Clip-on or waist-belt pouch with a magnetic or hinged opening you can use with one hand. One each.",
      },
      {
        id: 'whistle',
        name: "Gundog whistle",
        why: "Cockers respond brilliantly to a whistle. It sounds the same whoever blows it and carries much further than your voice.",
        priority: 'recommended',
        cocker: true,
        price: "£5–10",
        lookFor: "ACME 210.5, the classic gundog whistle. Buy two identical ones, one each, with lanyards.",
      },
      {
        id: 'reflective-light',
        name: "Collar light or reflective gear",
        why: "An autumn puppy spends a lot of time outside in the dark. Being seen keeps him safe.",
        priority: 'recommended',
        beforeHome: true,
        price: "£5–15",
        lookFor: "Rechargeable clip-on LED light for the collar or harness, and a reflective lead or harness.",
      },
      {
        id: 'car-restraint',
        name: "Car crate or car harness",
        why: "Highway Code rule 57 requires dogs to be suitably restrained in the car. You’ll need it to bring him home.",
        priority: 'essential',
        beforeHome: true,
        price: "£25–100",
        lookFor: "A secured travel crate for the boot, or a crash-tested car harness with a seat belt clip. A dog guard is an alternative for the boot.",
      },
    ],
  },
  {
    id: 'grooming',
    title: "Grooming",
    items: [
      {
        id: 'slicker-brush',
        name: "Slicker brush",
        why: "Brushes out tangles in his feathering. Daily brushing keeps mats away.",
        priority: 'essential',
        cocker: true,
        price: "£8–15",
        lookFor: "Small, soft-pinned slicker brush for puppies.",
      },
      {
        id: 'metal-comb',
        name: "Metal comb",
        why: "Finds knots behind the ears and in the feathering that a brush misses.",
        priority: 'essential',
        cocker: true,
        price: "£6–12",
        lookFor: "Wide and narrow-toothed steel comb.",
      },
      {
        id: 'dog-towel',
        name: "Dog towel or drying coat",
        why: "Wet, muddy spaniels need a good rub down after most walks in a UK winter.",
        priority: 'recommended',
        price: "£10–30",
        lookFor: "Microfibre towel or drying coat. A drying coat will fit better once he’s older.",
      },
      {
        id: 'ear-cleaner',
        name: "Ear cleaner",
        why: "Cockers are prone to ear infections. Keeping them clean and dry helps.",
        priority: 'recommended',
        cocker: true,
        price: "£8–15",
        lookFor: "A gentle dog ear cleaner your vet recommends. Don’t pluck ear hair routinely unless your vet advises it.",
      },
      {
        id: 'cotton-pads',
        name: "Cotton pads",
        why: "For wiping the outer ear and around the eyes.",
        priority: 'recommended',
        cocker: true,
        price: "£2–4",
        lookFor: "Plain cotton pads. Never cotton buds inside the ear.",
      },
      {
        id: 'nail-clippers',
        name: "Nail clippers or grinder",
        why: "Nails need trimming every few weeks. Getting him used to them early saves stress later.",
        priority: 'recommended',
        price: "£8–30",
        lookFor: "Small scissor-style clippers with a safety guard, or a quiet grinder. A styptic powder in case of a nick.",
      },
      {
        id: 'toothbrush',
        name: "Toothbrush and dog toothpaste",
        why: "Dental disease is the most common problem in cockers. Daily brushing is the best prevention.",
        priority: 'essential',
        cocker: true,
        price: "£5–10",
        lookFor: "Finger brush for now, soft dog toothbrush later. Only dog toothpaste; human toothpaste can contain xylitol.",
      },
      {
        id: 'shampoo',
        name: "Puppy shampoo",
        why: "For the inevitable fox poo roll. Don’t bath too often.",
        priority: 'optional',
        price: "£6–12",
        lookFor: "Gentle, soap-free puppy shampoo.",
      },
      {
        id: 'grooming-table',
        name: "Grooming table",
        why: "Makes brushing and trimming easier on your back and his feet feel secure.",
        priority: 'optional',
        cocker: true,
        price: "£40–100",
        lookFor: "A folding table with a non-slip top. A non-slip mat on a sturdy table works too.",
      },
      {
        id: 'grooming-scissors',
        name: "Round-ended grooming scissors",
        why: "For tidying the fur between his paw pads and around his feet, where mud and grass seeds collect.",
        priority: 'optional',
        cocker: true,
        price: "£8–15",
        lookFor: "Small round-tipped (safety) scissors. Leave the full trim to a groomer.",
      },
    ],
  },
  {
    id: 'toys',
    title: "Toys and enrichment",
    items: [
      {
        id: 'kong',
        name: "Kong Puppy",
        why: "Stuff with his breakfast or a bit of wet food, freeze it, and he has a calm, long-lasting activity in his crate.",
        priority: 'essential',
        beforeHome: true,
        price: "£8–15",
        lookFor: "Kong Puppy, small or medium. Buy two so one can be in the freezer.",
      },
      {
        id: 'lick-mat',
        name: "Lick mat",
        why: "Licking is calming. Brilliant for grooming, nail trims and alone-time practice.",
        priority: 'recommended',
        price: "£6–12",
        lookFor: "Dishwasher-safe, textured rubber mat. A suction version stays on the bath or wall.",
      },
      {
        id: 'snuffle-mat',
        name: "Snuffle mat",
        why: "Uses his nose to find his kibble. Great spaniel brain work.",
        priority: 'recommended',
        cocker: true,
        price: "£10–25",
        lookFor: "Washable fleece snuffle mat. Supervise so he doesn’t chew the strips.",
      },
      {
        id: 'tug-toy',
        name: "Tug toy",
        why: "Great for play and for teaching “Drop”. Gives nipping teeth somewhere to go.",
        priority: 'recommended',
        beforeHome: true,
        price: "£5–12",
        lookFor: "A long fleece or rope tug, so your hands are well away from his teeth.",
      },
      {
        id: 'soft-toys',
        name: "Soft toys",
        why: "Cockers love carrying things. Soft toys give him something to hold and present to you.",
        priority: 'recommended',
        cocker: true,
        price: "£5–10",
        lookFor: "Tough, puppy-sized soft toys without small parts. Check for damage often.",
      },
      {
        id: 'teething-toys',
        name: "Teething toys (freezable)",
        why: "Chewing something cold soothes sore gums when his adult teeth come through (about 3½–7 months).",
        priority: 'recommended',
        price: "£5–15",
        lookFor: "Puppy rubber teething toys you can freeze, or a knotted wet flannel frozen.",
      },
      {
        id: 'puzzle-feeder',
        name: "Puzzle feeder",
        why: "Mental exercise that tires him out, without strain on his growing joints.",
        priority: 'optional',
        price: "£10–25",
        lookFor: "An easy level-one puzzle to start with.",
      },
      {
        id: 'puppy-dummy',
        name: "Puppy training dummy",
        why: "A small canvas dummy for gentle retrieve games, the gundog way.",
        priority: 'optional',
        cocker: true,
        price: "£5–10",
        lookFor: "Puppy-sized (about 250 g) canvas dummy.",
      },
    ],
  },
  {
    id: 'home',
    title: "Home",
    items: [
      {
        id: 'door-grab-bag',
        name: "Grab bag by the front door",
        why: "In a flat every toilet trip is a dash downstairs. Keep lead, harness, poo bags, treats and kitchen roll ready at the door so you’re out in seconds.",
        priority: 'essential',
        beforeHome: true,
        price: "£5–10",
        lookFor: "A hook and a small bag or basket by the door.",
      },
      {
        id: 'door-pads',
        name: "A few puppy pads (backup only)",
        why: "One pad by the front door for emergencies and overnight while he’s tiny. A bridge, not the plan — outside is the goal.",
        priority: 'recommended',
        beforeHome: true,
        price: "£8–12",
        lookFor: "Unscented pads; use one spot only, away from his bed and bowls.",
      },
      {
        id: 'baby-gates',
        name: "Baby gates",
        why: "Keep him off the stairs and out of rooms, and let you start alone-time training.",
        priority: 'essential',
        beforeHome: true,
        price: "£20–40 each",
        lookFor: "Pressure-fit gates with a walk-through door. One for the bottom of the stairs, one for the kitchen.",
      },
      {
        id: 'enzyme-cleaner',
        name: "Enzyme cleaner",
        why: "Removes the smell of wee so he won’t go back to the same spot. Ammonia cleaners smell like wee to a dog.",
        priority: 'essential',
        beforeHome: true,
        price: "£6–15",
        lookFor: "A pet enzyme or biological cleaner spray. Biological washing powder in water works too.",
      },
      {
        id: 'kitchen-roll',
        name: "Kitchen roll",
        why: "For mopping up accidents. You’ll need lots.",
        priority: 'essential',
        beforeHome: true,
        price: "£3–6",
        lookFor: "A big multipack.",
      },
      {
        id: 'old-towels',
        name: "Old towels",
        why: "For muddy paws, wet puppies, the car and covering the sofa.",
        priority: 'recommended',
        beforeHome: true,
        price: "Free",
        lookFor: "Raid the airing cupboard. Keep a pile by the front door.",
      },
      {
        id: 'cable-protectors',
        name: "Cable protectors",
        why: "Chewed cables can cause burns or electric shocks.",
        priority: 'recommended',
        beforeHome: true,
        price: "£8–15",
        lookFor: "Split cable tubing or covers for anything at puppy height, especially the tablet charger.",
      },
      {
        id: 'bitter-spray',
        name: "Bitter spray",
        why: "Can discourage chewing of furniture legs, but management and chew toys matter more.",
        priority: 'optional',
        price: "£5–10",
        lookFor: "Pet-safe bitter apple spray. Test on a small area first.",
      },
      {
        id: 'slug-control',
        name: "Pet-safe slug control",
        why: "Slugs and snails carry lungworm, and metaldehyde slug pellets are dangerous for dogs.",
        priority: 'recommended',
        cocker: true,
        price: "£5–15",
        lookFor: "Wool pellets, copper tape or beer traps. Never metaldehyde pellets, and pick up toys and bowls from the garden at night.",
      },
    ],
  },
  {
    id: 'health',
    title: "Health and insurance",
    items: [
      {
        id: 'insurance',
        name: "Pet insurance",
        why: "Insure from day one, before any problems appear. A lifetime policy suits a breed prone to ear, eye and skin problems.",
        priority: 'essential',
        beforeHome: true,
        price: "£25–60 a month",
        lookFor: "A lifetime policy with a high annual vet-fee limit. Check the waiting periods, and whether the breeder’s free cover is included.",
      },
      {
        id: 'first-aid-kit',
        name: "Dog first-aid kit",
        why: "For cuts, thorns and sore paws on walks, and to hold things together until you get to the vet.",
        priority: 'recommended',
        price: "£15–30",
        lookFor: "Bandages, non-stick dressings, saline, blunt scissors, tweezers and a foil blanket. Save the vet and Animal PoisonLine numbers in your phones.",
      },
      {
        id: 'tick-twister',
        name: "Tick remover",
        why: "Ticks are active March–October and carry Lyme disease. Removing them promptly and properly matters.",
        priority: 'recommended',
        price: "£3–6",
        lookFor: "Tick twister or tick hook. Keep one on the lead and one at home.",
      },
      {
        id: 'parasite-control',
        name: "Vet-approved flea, worm and lungworm treatment",
        why: "Lungworm is a risk across much of the UK and only prescription products cover it. Monthly treatment is best for a scavenging cocker.",
        priority: 'essential',
        cocker: true,
        price: "£10–20 a month",
        lookFor: "Ask your vet. Many practices offer a monthly health plan that includes this and his boosters.",
      },
      {
        id: 'pet-carrier',
        name: "Pet carrier or sling",
        why: "Essential in a flat: you’ll carry him down the stairs for every toilet trip, to the vet, and around the city to see the world before he can go on the ground.",
        priority: 'essential',
        beforeHome: true,
        price: "£20–40",
        lookFor: "A front sling or small carrier. He’ll outgrow it quickly, so borrow one if you can.",
      },
    ],
  },
  {
    id: 'running',
    title: "Running together (from about 12 months)",
    items: [
      {
        id: 'canicross-harness',
        name: "Running harness",
        why: "Spreads the pull across his chest when you run together — his walking Y-harness isn’t built for it.",
        priority: 'optional',
        price: "£35–90",
        lookFor: "An X-back or H-back canicross harness. Buy at about 12 months, measured when he’s fully grown.",
      },
      {
        id: 'bungee-line',
        name: "Bungee running line",
        why: "Takes the jolt out of sudden stops and squirrel moments, for his neck and your back.",
        priority: 'optional',
        price: "£20–45",
        lookFor: "About 2 m with a bungee section, clipping to a waist belt.",
      },
      {
        id: 'waist-belt',
        name: "Running waist belt",
        why: "Hands free, and the pull goes through your hips rather than your shoulder.",
        priority: 'optional',
        price: "£35–90",
        lookFor: "A canicross belt with leg loops so it doesn’t ride up.",
      },
      {
        id: 'running-bottle',
        name: "Water on the run",
        why: "He needs a drink every 15–20 minutes, more when it’s mild.",
        priority: 'optional',
        price: "£12–20",
        lookFor: "A soft flask with a bowl, or a collapsible bowl that clips on.",
      },
      {
        id: 'paw-protection',
        name: "Paw protection",
        why: "Pavements, grit and salt are hard on pads.",
        priority: 'optional',
        price: "£12–55",
        lookFor: "Paw wax for everyday; boots only for rough or icy ground.",
      },
      {
        id: 'hi-vis',
        name: "Hi-vis or a light",
        why: "Glasgow winter runs are mostly in the dark.",
        priority: 'optional',
        price: "£15–60",
        lookFor: "A clip-on LED light, or a hi-vis vest for him.",
      },
    ],
  },
  {
    id: 'tech',
    title: "Smart kit for Arlo HQ",
    items: [
      {
        id: 'nfc-tags',
        name: "NFC sticker tags (NTAG215)",
        why: "Stick one inside the front door and tap your phone to log “wee outside” in a second. Android opens the tag’s link directly; iPhones need a Shortcuts automation.",
        priority: 'recommended',
        beforeHome: true,
        price: "about £8 for 10",
        lookFor: "NTAG215 sticker tags. Round stickers work well on a door frame; use an on-metal type if it’s going on metal.",
      },
      {
        id: 'flic-buttons',
        name: "Flic 2 buttons",
        why: "Press by the front door to log a wee or poo without touching the tablet. Single, double and hold can mean different things.",
        priority: 'optional',
        price: "£30–35 each",
        lookFor: "Flic 2 buttons. Two is a good start: one by the front door, one by the crate.",
      },
      {
        id: 'flic-hub',
        name: "Flic Hub Mini or Flic Hub LR",
        why: "Lets the Flic buttons post straight to the app’s API with no phone needed.",
        priority: 'optional',
        price: "£50–100",
        lookFor: "Flic Hub Mini or LR. Use its “Internet Request” action to POST to the app’s ingest address.",
      },
      {
        id: 'tablet-mount',
        name: "Rotating tablet wall mount",
        why: "Puts Arlo HQ at eye level in the kitchen, and lets you turn it between portrait and landscape.",
        priority: 'recommended',
        price: "£25–120",
        lookFor: "A magnetic rotating dock or a rotating clip mount that fits the Galaxy Tab S9 FE. Leave an air gap behind it.",
      },
      {
        id: 'usb-c-socket',
        name: "USB-C wall socket",
        why: "Keeps the wall tablet charged without trailing cables that a puppy could chew.",
        priority: 'recommended',
        price: "£15–30",
        lookFor: "UK wall socket with a USB-C PD port, 15–25 W. Turn on Battery protection (80%) on the tablet.",
      },
      {
        id: 'gps-tracker',
        name: "Tractive GPS DOG 6",
        why: "GPS tracking if he ever runs off, and his activity and sleep minutes can flow into Arlo HQ via Home Assistant.",
        priority: 'optional',
        cocker: true,
        price: "£40–50 plus subscription",
        lookFor: "Tractive GPS DOG 6 with a Premium plan (needed for the Home Assistant integration). Wait until he’s big enough for the collar clip.",
      },
      {
        id: 'camera',
        name: "Indoor camera",
        why: "Watch him during alone-time training, so you know when to come back before he gets upset.",
        priority: 'recommended',
        price: "about £25",
        lookFor: "Tapo C200 or C210 pan-and-tilt camera. It has an official Home Assistant integration. Mount it out of reach.",
      },
      {
        id: 'ha-hub',
        name: "Home Assistant Green or Raspberry Pi",
        why: "Optional hub that glues trackers, cameras and buttons to the app.",
        priority: 'optional',
        price: "£60–130",
        lookFor: "Home Assistant Green is the easy option; a Raspberry Pi 4 or 5 with an SSD works too.",
      },
      {
        id: 'baby-scale',
        name: "Baby or parcel scale",
        why: "Weighing him every 2–4 weeks up to 6 months shows he’s growing well and keeps his food right.",
        priority: 'optional',
        price: "£15–30",
        lookFor: "Digital baby or parcel scale up to 20 kg. Later, weigh yourself holding him and subtract your own weight, or use the vet’s free weigh-ins.",
      },
    ],
  },
];

/** The catalogue, with real products to buy (a good-value and a premium one) for each item. */
export const SHOPPING: ShoppingCategory[] = CATALOGUE.map((c) => ({
  ...c,
  items: c.items.map((i) => (PRODUCTS[i.id] ? { ...i, options: PRODUCTS[i.id] } : i)),
}));
